import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { FlaskConical, X } from "lucide-react";
import { ApiError } from "../../api/client";
import { endsAtText, TEST_AS_QK, testAsApi, testAsPrefix, type TestAsSession } from "../../lib/testAs/testAs";

// "Test as role…" (Admin only, O-R165–O-R170): pick Planner, Purchaser, Purchasing Manager, Logistics or Shipping Manager and
// use that role's screens and buttons as yourself, for a few hours at most. Separate from "View as role" (read-only). The
// server decides who may (a real Admin, after the authenticator step), adds the role on every request and records every act
// as the Admin's own, "tested as". Below the picker: the active and recent test sessions.

const STATE_LABEL: Record<TestAsSession["state"], string> = { active: "Running", stopped: "Stopped", expired: "Time up" };

function when(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", timeZone: "America/New_York" });
  return `${day} ${endsAtText(iso)}`;
}

export function TestAsDialog({ onClose }: { onClose: () => void }) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const status = useQuery({ queryKey: TEST_AS_QK.status, queryFn: testAsApi.status, staleTime: 30_000, retry: false });
  const sessions = useQuery({
    queryKey: TEST_AS_QK.sessions,
    queryFn: testAsApi.sessions,
    enabled: !!status.data?.canTestAs,
    staleTime: 30_000,
    retry: false
  });

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const current = status.data?.active ? status.data : null;

  const start = async (role: string) => {
    setBusy(role);
    setError(null);
    try {
      // One session at a time: switching roles stops the running one first (audited).
      if (current) await testAsApi.stop();
      await testAsApi.start(role);
      onClose();
      // Reload so the menu, the start page and every cached page follow the added role from a clean start.
      window.location.assign("/");
    } catch (e) {
      setBusy(null);
      setError(e instanceof ApiError ? e.message : "Testing as that role could not start. Try again.");
    }
  };

  const roles = status.data?.roles ?? [];
  const hours = status.data?.maxHours ?? 4;

  return createPortal(
    <div className="fixed inset-0 z-[95] flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 pt-16 print:hidden" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="w-full max-w-xl rounded-xl border border-slate-200 bg-white p-4 shadow-2xl" data-testid="test-as-dialog">
        <div className="flex items-start justify-between gap-2">
          <h2 id={titleId} className="flex items-center gap-1.5 text-base font-semibold text-slate-900">
            <FlaskConical aria-hidden="true" className="h-4 w-4 text-brand-700" />
            Test as role
          </h2>
          <button ref={closeRef} type="button" className="rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Close" onClick={onClose} data-testid="test-as-close">
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1 text-sm text-slate-700">
          Work through the practice as this role, as yourself. You keep your Admin permissions and the role&apos;s screens and buttons also work.
          <strong className="font-semibold"> Every action is real and signed by you</strong>, recorded as &ldquo;tested as&rdquo; the role. The role&apos;s
          emails, Teams messages and notices also come to you, marked &ldquo;{testAsPrefix("Purchaser")}&rdquo; and so on; the people who hold the role still get
          theirs. It ends after {hours} {hours === 1 ? "hour" : "hours"}, or when you choose Stop testing. To only look, <button type="button" className="table-link font-medium" onClick={() => { onClose(); window.dispatchEvent(new Event("summitops:open-view-as")); }}>open View as role</button>.
        </p>
        {current && (
          <p className="mt-2 rounded-lg border border-brand-300 bg-brand-50 px-3 py-2 text-sm text-brand-900" data-testid="test-as-current">
            You are testing as {current.roleLabel}
            {current.endsAtUtc ? ` until ${endsAtText(current.endsAtUtc)}` : ""}. Choosing another role stops this test first.
          </p>
        )}
        {status.isLoading && <p className="mt-3 text-sm text-slate-600">Loading the roles…</p>}
        {status.isError && <p className="mt-3 text-sm text-red-700">The roles could not be loaded. Close this and try again.</p>}
        {status.data && !status.data.canTestAs && <p className="mt-3 text-sm text-slate-700">Only an Admin can test as another role.</p>}
        {roles.length > 0 && (
          <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2" data-testid="test-as-roles" role="radiogroup" aria-label="Role to test as">
            {roles.map((r) => (
              <button
                key={r.role}
                type="button"
                role="radio"
                aria-checked={selectedRole === r.role}
                className={`btn-secondary w-full justify-start !px-3 !py-1.5 text-left text-sm disabled:opacity-60 ${selectedRole === r.role ? "border-brand-500 bg-brand-50 text-brand-900" : ""}`}
                disabled={busy !== null || current?.role === r.role}
                onClick={() => setSelectedRole(r.role)}
                data-testid={`test-as-role-${r.role}`}
              >
                {current?.role === r.role ? `${r.label} (now)` : r.label}
              </button>
            ))}
          </div>
        )}
        {status.data?.canTestAs && (
          <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-3">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn-primary" disabled={!selectedRole || busy !== null} onClick={() => selectedRole && void start(selectedRole)} data-testid="test-as-start">
              {busy ? "Starting test..." : `Start testing as ${roles.find((role) => role.role === selectedRole)?.label ?? "selected role"}`}
            </button>
          </div>
        )}
        {error && (
          <p role="alert" className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="test-as-error">
            {error}
          </p>
        )}
        {status.data?.canTestAs && (
          <section className="mt-4" aria-labelledby={`${titleId}-sessions`}>
            <h3 id={`${titleId}-sessions`} className="text-sm font-semibold text-slate-900">
              Test sessions
            </h3>
            {sessions.isLoading && <p className="mt-1 text-sm text-slate-600">Loading…</p>}
            {sessions.isError && <p className="mt-1 text-sm text-red-700">The test sessions could not be loaded.</p>}
            {sessions.data && sessions.data.length === 0 && <p className="mt-1 text-sm text-slate-600">No one has tested a role yet.</p>}
            {sessions.data && sessions.data.length > 0 && (
              <div className="mt-1 max-h-56 overflow-auto rounded-lg border border-slate-200">
                <table className="w-full text-left text-xs" data-testid="test-as-sessions">
                  <caption className="sr-only">Active and recent test sessions, newest first. Times are plant time.</caption>
                  <thead className="sticky top-0 bg-slate-50 text-slate-700">
                    <tr>
                      <th scope="col" className="px-2 py-1 font-semibold">Who</th>
                      <th scope="col" className="px-2 py-1 font-semibold">Role</th>
                      <th scope="col" className="px-2 py-1 font-semibold">Started</th>
                      <th scope="col" className="px-2 py-1 font-semibold">Ends / ended</th>
                      <th scope="col" className="px-2 py-1 font-semibold">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.data.map((s) => (
                      <tr key={s.sessionId} className="border-t border-slate-100 text-slate-800" data-testid={`test-as-session-${s.state}`}>
                        <td className="px-2 py-1">{s.displayName}</td>
                        <td className="px-2 py-1">{s.roleLabel}</td>
                        <td className="px-2 py-1 tabular-nums">{when(s.startedAtUtc)}</td>
                        <td className="px-2 py-1 tabular-nums">{when(s.endedAtUtc ?? s.endsAtUtc)}</td>
                        <td className="px-2 py-1">{STATE_LABEL[s.state] ?? s.state}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>
    </div>,
    document.body
  );
}
